import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  AlertTriangle, 
  Clock, 
  Users, 
  Shield, 
  FileText, 
  RefreshCw,
  CheckCircle,
  XCircle,
  ArrowRight,
  Zap,
  Bell,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  getJobsNeedingAttention, 
  getWorkflowStatus,
  supervisorOverride
} from '../services/api';

const WorkflowDashboardPage = () => {
  const { user, isSupervisor } = useAuth();
  const [attentionData, setAttentionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAttentionData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAttentionData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAttentionData = async () => {
    try {
      const response = await getJobsNeedingAttention();
      setAttentionData(response.data);
    } catch (error) {
      console.error('Failed to fetch attention data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAttentionData();
  };

  const handleOverride = async (woId, action) => {
    const justification = prompt(`Enter justification for ${action}:`);
    if (!justification) return;

    try {
      await supervisorOverride(woId, action, justification);
      toast.success(`${action} completed successfully`);
      fetchAttentionData();
    } catch (error) {
      toast.error(`Failed to ${action}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'canvassing': 'bg-blue-100 text-blue-700',
      'assigned': 'bg-purple-100 text-purple-700',
      'safety_pending': 'bg-yellow-100 text-yellow-700',
      'risk_review': 'bg-red-100 text-red-700',
      'ready': 'bg-green-100 text-green-700',
      'in_progress': 'bg-orange-100 text-orange-700',
      'completed': 'bg-slate-100 text-slate-700'
    };
    return statusColors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Zap className="w-7 h-7 text-green-600" />
            Workflow Command Center
          </h1>
          <p className="text-slate-600 mt-1">Real-time automation status and jobs requiring attention</p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={attentionData?.total_attention_needed > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-full ${attentionData?.total_attention_needed > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                {attentionData?.total_attention_needed > 0 ? (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
              </div>
              <div>
                <p className="text-3xl font-bold">{attentionData?.total_attention_needed || 0}</p>
                <p className="text-sm text-slate-600">Need Attention</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-red-100">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{attentionData?.risk_review_required?.length || 0}</p>
                <p className="text-sm text-slate-600">Risk Review</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{attentionData?.pending_crew_assignment?.length || 0}</p>
                <p className="text-sm text-slate-600">Pending Crew</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-yellow-100">
                <FileText className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{attentionData?.missing_safety_brief?.length || 0}</p>
                <p className="text-sm text-slate-600">Missing Safety</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Status Legend */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Workflow Status Pipeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-sm">Canvassing</span>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-sm">Assigned</span>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-sm">Safety Pending</span>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-sm">Risk Review</span>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm">Ready</span>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-sm">In Progress</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Risk Review Required */}
      {attentionData?.risk_review_required?.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="bg-red-50">
            <CardTitle className="flex items-center gap-2 text-red-800">
              <Shield className="w-5 h-5" />
              ⚠️ Risk Review Required ({attentionData.risk_review_required.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {attentionData.risk_review_required.map((wo) => (
                  <div key={wo.id} className="p-4 border border-red-200 rounded-lg bg-white">
                    <div className="flex items-start justify-between">
                      <div>
                        <Link to={`/work-orders/${wo.id}`} className="font-semibold text-red-800 hover:underline">
                          {wo.wo_number} - {wo.title}
                        </Link>
                        <p className="text-sm text-slate-600 mt-1">{wo.location_name}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge className={getStatusBadge(wo.workflow_status)}>{wo.workflow_status}</Badge>
                          {wo.risk_assessment?.risks?.map((risk, i) => (
                            <Badge key={i} variant="outline" className="text-red-600 border-red-300">
                              {risk.type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {isSupervisor() && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-green-600 border-green-300"
                            onClick={() => handleOverride(wo.id, 'approve_risk')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 border-red-300"
                            onClick={() => handleOverride(wo.id, 'cancel')}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Pending Crew Assignment */}
      {attentionData?.pending_crew_assignment?.length > 0 && (
        <Card className="border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Users className="w-5 h-5" />
              Pending Crew Assignment ({attentionData.pending_crew_assignment.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {attentionData.pending_crew_assignment.slice(0, 5).map((wo) => (
                <div key={wo.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Link to={`/work-orders/${wo.id}`} className="font-medium hover:underline">
                      {wo.wo_number} - {wo.title}
                    </Link>
                    <p className="text-sm text-slate-500">{wo.location_name} • Created: {new Date(wo.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700">
                    <Clock className="w-3 h-3 mr-1" />
                    Awaiting Crew
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Missing Safety Brief */}
      {attentionData?.missing_safety_brief?.length > 0 && (
        <Card className="border-yellow-200">
          <CardHeader className="bg-yellow-50">
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <FileText className="w-5 h-5" />
              Missing Safety Brief ({attentionData.missing_safety_brief.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {attentionData.missing_safety_brief.slice(0, 5).map((wo) => (
                <div key={wo.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Link to={`/work-orders/${wo.id}`} className="font-medium hover:underline">
                      {wo.wo_number} - {wo.title}
                    </Link>
                    <p className="text-sm text-slate-500">Start: {wo.start_date || 'TBD'}</p>
                  </div>
                  <Link to={`/work-orders/${wo.id}?tab=stewie`}>
                    <Button size="sm" variant="outline" className="text-yellow-700 border-yellow-300">
                      Generate Safety Brief
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unacknowledged Bulletins */}
      {attentionData?.unacknowledged_bulletins?.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center gap-2 text-orange-800">
              <Bell className="w-5 h-5" />
              Unacknowledged High-Priority Bulletins ({attentionData.unacknowledged_bulletins.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {attentionData.unacknowledged_bulletins.map((bulletin) => (
                <div key={bulletin.id} className="p-3 border border-orange-200 rounded-lg bg-orange-50/50">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-orange-800">{bulletin.title}</p>
                      <p className="text-sm text-slate-600 mt-1">{bulletin.message?.slice(0, 100)}...</p>
                    </div>
                    <Badge className="bg-red-100 text-red-700">{bulletin.priority}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Clear Message */}
      {attentionData?.total_attention_needed === 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-green-800">All Systems Operational</h3>
            <p className="text-green-700 mt-2">No jobs currently require immediate attention.</p>
            <p className="text-sm text-green-600 mt-1">Last checked: {new Date(attentionData?.generated_at).toLocaleString()}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WorkflowDashboardPage;
